import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  Card,
  Cifra,
  FilaCifras,
  Aviso,
  COLOR,
  OTROS,
  eur,
  eur0,
  pct,
  num,
  mesCorto,
  ejes,
  leyenda,
  segmento,
  linea,
  useEndpoint,
} from '../ui';

/**
 * Margen: el coste estimado de cada cliente (el mismo de la pantalla Costes)
 * frente a lo que factura (la facturación de la sección Dinero, que llega ya
 * cargada: no se vuelve a pedir).
 */
const P = '/portfolio-performance';
const round2 = x => Math.round(x * 100) / 100;

export default function MargenSection({ params, billing }) {
  const margen = useEndpoint(`${P}/margin`, params);
  const m = margen.data;
  const b = billing.data;
  const listo = margen.estado === 'ok' && billing.estado === 'ok' && m && b;

  const { porMes, porCliente, totales, mesesSinTarifa } = useMemo(() => {
    if (!listo) return { porMes: [], porCliente: [], totales: null, mesesSinTarifa: [] };
    // Honorarios de recobro y de AR: el coste variable de cada cliente incluye los
    // dos (WhatsApp oficial, IA), así que el margen tiene que llevar los dos.
    const feeMes = new Map(b.by_month.map(x => [x.month, x.fee + (x.fee_ar || 0)]));
    const cobradoMes = new Map(b.by_month.map(x => [x.month, x.collected_extrajudicial + x.collected_judicial]));
    const pm = m.months.map(x => ({
      mes: x.month,
      variable: x.variable_eur,
      fijo: x.fixed_eur,
      honorarios: feeMes.get(x.month) || 0,
      atendidos: x.attended,
      con_tarifa: x.rates_loaded && x.variable_eur > 0,
    }));
    // Por cliente: sus honorarios del periodo contra su coste variable del periodo.
    const coste = new Map();
    for (const x of m.months)
      for (const c of x.companies) coste.set(c.company_id, (coste.get(c.company_id) || 0) + c.variable_eur);
    const ids = new Set([...coste.keys(), ...b.companies.map(c => c.company_id)]);
    const pc = [...ids].map(id => {
      const cb = b.companies.find(c => c.company_id === id);
      const fee = cb ? (cb.has_recovery && !cb.has_pricing ? null : cb.fee_total) : null;
      const cv = round2(coste.get(id) || 0);
      return {
        cliente: cb?.name || m.names[id] || id,
        honorarios: fee,
        coste_variable: cv,
        margen: fee == null ? null : round2(fee - cv),
        coste_sobre_cobrado: cb?.collected ? round2((cv / cb.collected) * 100) : null,
        pct_coste_sobre_honorarios: fee ? round2((cv / fee) * 100) : null,
      };
    });
    pc.sort((x, y) => (y.honorarios ?? -1) - (x.honorarios ?? -1));
    const variable = pm.reduce((t, x) => t + x.variable, 0);
    const fijo = pm.reduce((t, x) => t + (x.fijo || 0), 0);
    const honorarios = pm.reduce((t, x) => t + x.honorarios, 0);
    const cobrado = [...cobradoMes.values()].reduce((t, v) => t + v, 0);
    const conCoste = pm.filter(x => x.con_tarifa);
    const atendidos = conCoste.reduce((t, x) => t + x.atendidos, 0);
    return {
      porMes: pm,
      porCliente: pc,
      totales: {
        variable: round2(variable),
        fijo: params.clients ? null : round2(fijo),
        honorarios: round2(honorarios),
        margen: round2(honorarios - variable - (params.clients ? 0 : fijo)),
        coste_por_atendido: atendidos ? round2((variable + (params.clients ? 0 : fijo)) / atendidos) : null,
        coste_por_euro: cobrado ? round2((variable + (params.clients ? 0 : fijo)) / cobrado) : null,
      },
      mesesSinTarifa: pm.filter(x => !x.con_tarifa).map(x => x.mes),
    };
  }, [listo, m, b, params.clients]);

  const cargando = margen.estado === 'loading' || billing.estado === 'loading';
  const estado = margen.estado === 'error' || billing.estado === 'error' ? 'error' : listo ? 'ok' : 'loading';

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra
          etiqueta='Honorarios'
          cargando={cargando}
          valor={totales ? eur0(totales.honorarios) : '—'}
          pie='Recobro y AR, con los precios del sistema'
        />
        <Cifra
          etiqueta='Coste variable'
          cargando={cargando}
          valor={totales ? eur0(totales.variable) : '—'}
          pie={totales?.fijo != null ? `Más ${eur0(totales.fijo)} de fijo` : 'El fijo solo con «Todos»'}
        />
        <Cifra
          etiqueta={params.clients ? 'Margen de contribución' : 'Margen tras el fijo'}
          cargando={cargando}
          valor={totales ? eur0(totales.margen) : '—'}
          tono={totales && totales.margen < 0 ? 'alerta' : 'neutral'}
        />
        <Cifra
          etiqueta='Coste por expediente atendido'
          cargando={cargando}
          valor={totales ? eur(totales.coste_por_atendido) : '—'}
          pie='Al mes, en los meses con tarifa'
        />
        <Cifra
          etiqueta='Coste por euro cobrado'
          cargando={cargando}
          valor={totales ? eur(totales.coste_por_euro) : '—'}
        />
      </FilaCifras>

      {listo && mesesSinTarifa.length > 0 && (
        <Aviso>
          Sin coste variable por cliente en {mesesSinTarifa.map(mesCorto).join(', ')}: el reparto de la pantalla Costes
          empieza en septiembre de 2026. En esos meses el margen de cada cliente sale igual a sus honorarios, y no lo
          es. Para comparar clientes, mira de septiembre en adelante. Ver{' '}
          <a className='underline' href='/costs'>
            Costes
          </a>
          .
        </Aviso>
      )}

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <Card
          titulo='¿Cuánto cuesta servir a cada cliente frente a lo que factura?'
          lectura='Por cliente, sus honorarios del periodo, su coste variable y lo que queda. Un margen negativo es un cliente que cuesta más de lo que deja.'
          nota='Coste variable = lo que generan sus expedientes (llamadas, SMS, WhatsApp oficial, IA), con las tarifas de Costes. Lo fijo (personas, cuotas) no se reparte: sale en el total con «Todos».'
          estado={estado}
          vacio={!porCliente.length}
          filas={porCliente}
          csv='margen-por-cliente'
          columnas={[
            { key: 'cliente', label: 'Cliente' },
            { key: 'honorarios', label: 'Honorarios', fmt: eur },
            { key: 'coste_variable', label: 'Coste variable', fmt: eur },
            { key: 'margen', label: 'Margen', fmt: eur },
            { key: 'pct_coste_sobre_honorarios', label: 'Coste / honorarios', fmt: pct },
            { key: 'coste_sobre_cobrado', label: 'Coste / cobrado', fmt: pct },
          ]}
          alto={null}
          ancho
          maxFilas={30}
        />
        <Card
          titulo='¿Cubren los honorarios lo que cuesta la operación cada mes?'
          lectura='Barras: el coste del mes (variable y, con «Todos», el fijo). Línea: los honorarios. Si la línea va por encima, el mes deja margen.'
          nota='Misma unidad y escala: euros al mes.'
          estado={estado}
          vacio={!porMes.length}
          filas={porMes}
          csv='coste-frente-a-honorarios'
          columnas={[
            { key: 'mes', label: 'Mes', fmt: mesCorto },
            { key: 'variable', label: 'Coste variable', fmt: eur },
            { key: 'fijo', label: 'Coste fijo', fmt: eur },
            { key: 'honorarios', label: 'Honorarios', fmt: eur },
            { key: 'atendidos', label: 'Expedientes atendidos', fmt: num },
          ]}
          ancho
        >
          <Chart
            type='bar'
            data={{
              labels: porMes.map(x => mesCorto(x.mes)),
              datasets: [
                linea(
                  'Honorarios',
                  porMes.map(x => x.honorarios),
                  COLOR.s2,
                  { stack: 'honorarios' }
                ),
                segmento(
                  'Coste variable',
                  porMes.map(x => x.variable),
                  COLOR.s1
                ),
                ...(params.clients
                  ? []
                  : [
                      segmento(
                        'Coste fijo',
                        porMes.map(x => x.fijo || 0),
                        OTROS
                      ),
                    ]),
              ],
            }}
            options={{
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: leyenda,
                tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${eur(ctx.parsed.y)}` } },
              },
              scales: {
                ...ejes('€'),
                x: { stacked: true, grid: { display: false } },
                y: { ...ejes('€').y, stacked: true },
              },
            }}
          />
        </Card>
      </div>
    </div>
  );
}
